// Subscription and billing service using Stripe

import { loadStripe, Stripe } from '@stripe/stripe-js';
import { supabase } from './supabase';
import { PLAN_LIMITS, PLAN_PRICING } from './types';

let stripePromise: Promise<Stripe | null> | null = null;

export function getStripe(): Promise<Stripe | null> {
  if (!stripePromise) {
    const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    stripePromise = key ? loadStripe(key) : Promise.resolve(null);
  }
  return stripePromise;
}

export type PlanType = 'free' | 'professional' | 'team' | 'enterprise';

export interface SubscriptionInfo {
  plan: PlanType;
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export async function getCurrentSubscription(userId: string): Promise<SubscriptionInfo | null> {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .in('status', ['active', 'trialing', 'past_due', 'canceled'])
      .maybeSingle();

    if (error) throw error;

    if (!data) {
      return {
        plan: 'free',
        status: 'active',
        currentPeriodEnd: null,
        cancelAtPeriodEnd: false
      };
    }

    const now = new Date();
    const periodEnd = data.current_period_end ? new Date(data.current_period_end) : null;

    if (data.status === 'canceled' && periodEnd && periodEnd < now) {
      return {
        plan: 'free',
        status: 'canceled',
        currentPeriodEnd: data.current_period_end,
        cancelAtPeriodEnd: false
      };
    }

    if (data.status === 'canceled' && periodEnd && periodEnd >= now) {
      return {
        plan: data.plan_type as PlanType,
        status: 'active',
        currentPeriodEnd: data.current_period_end,
        cancelAtPeriodEnd: true
      };
    }

    return {
      plan: data.plan_type as PlanType,
      status: data.status,
      currentPeriodEnd: data.current_period_end,
      cancelAtPeriodEnd: data.cancel_at_period_end
    };
  } catch (error) {
    console.error('Error fetching subscription:', error);
    return null;
  }
}

export async function checkUsageLimit(userId: string, limitType: 'requests' | 'analyses' = 'requests'): Promise<{
  canProceed: boolean;
  current: number;
  limit: number;
  planType: PlanType;
}> {
  try {
    const subscription = await getCurrentSubscription(userId);

    if (!subscription) {
      throw new Error('Unable to verify subscription status');
    }

    const planType = subscription.plan;
    const limits = PLAN_LIMITS[planType];

    if (limitType === 'requests') {
      const { count, error } = await supabase
        .from('feedback_requests')
        .select('*', { count: 'exact', head: true })
        .eq('requester_id', userId);

      if (error) throw error;

      const current = count || 0;
      const limit = limits.requests_per_month;

      return {
        canProceed: limit === -1 || current < limit,
        current,
        limit: limit === -1 ? Infinity : limit,
        planType
      };
    } else {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const { count, error } = await supabase
        .from('feedback_summaries')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gte('last_processed_at', startOfMonth.toISOString());

      if (error) throw error;

      const current = count || 0;
      const limit = limits.ai_analyses_per_month;

      return {
        canProceed: limit === -1 || current < limit,
        current,
        limit: limit === -1 ? Infinity : limit,
        planType
      };
    }
  } catch (error) {
    console.error('Error checking usage limit:', error);
    throw error;
  }
}

export async function createCheckoutSession(
  userId: string,
  planType: PlanType,
  billingPeriod: 'monthly' | 'annual'
): Promise<{ sessionId: string; url: string } | null> {
  try {
    if (!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) {
      console.warn('Stripe not configured');
      throw new Error('Stripe is not configured. Please check your environment variables.');
    }

    const session = await supabase.auth.getSession();
    if (!session.data.session?.access_token) {
      throw new Error('Authentication required. Please sign in again.');
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.data.session.access_token}`
      },
      body: JSON.stringify({
        userId,
        planType,
        billingPeriod
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || 'Failed to create checkout session');
    }

    const { sessionId, url } = await response.json();

    if (!sessionId || !url) {
      throw new Error('Invalid response from checkout session');
    }

    return { sessionId, url };
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
}

export async function redirectToCheckout(url: string): Promise<void> {
  if (!url) {
    throw new Error('Checkout URL is required');
  }

  const stripe = await getStripe();
  if (!stripe) {
    throw new Error('Stripe not initialized. Please check your configuration.');
  }

  window.location.href = url;
}

export async function createCustomerPortalSession(userId: string): Promise<string | null> {
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-portal-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
      },
      body: JSON.stringify({ userId })
    });

    if (!response.ok) {
      throw new Error('Failed to create portal session');
    }

    const { url } = await response.json();
    return url;
  } catch (error) {
    console.error('Error creating portal session:', error);
    return null;
  }
}

export function getPlanFeatures(planType: PlanType): string[] {
  return PLAN_LIMITS[planType].features;
}

export function getPlanPrice(planType: PlanType, billingPeriod: 'monthly' | 'annual'): number {
  return PLAN_PRICING[planType][billingPeriod];
}

export function canAccessFeature(planType: PlanType, feature: string): boolean {
  const features = PLAN_LIMITS[planType].features;
  return features.includes(feature) || features.includes('all_' + planType);
}
